import { Route, Routes, Navigate, useLocation } from 'react-router';
import { useRoleRoutes } from '../hooks/use-role-routes';
import { useAuth } from '@/hooks/auth/use-auth';
import { Loading } from '@/components/ui/loading';
import AuthLayout from '@/layouts/auth-layout';
import MainLayout from '@/layouts/main-layout';
import LoginPage from '@/pages/auth/login/page';
import NotFoundPage from '@/pages/not-found/page';
import OnboardingPage from '@/pages/onboarding/page';
import { Suspense } from 'react';
import { PermissionLoader } from '@/components/ui/permission-loader';
import { RouteLoader } from '@/components/ui/route-loader';

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading message="Checking authentication..." />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/auth/login" replace />;
}

function GuestGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading message="Checking authentication..." />;
  }

  return !isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
}

function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  // If it's first login and not already on onboarding page, redirect to onboarding
  if (user?.flag === 'True' && !location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding" replace />;
  }

  // If it's not first login and on onboarding page, redirect to root
  if (user?.flag !== 'True' && location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function RoleGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();

  // Only redirect if we're at the root path
  if (location.pathname === '/') {
    switch (user?.user_type) {
      case 'school_admin':
        return <Navigate to="/admin/dashboard" replace />;
      case 'teacher':
        return <Navigate to="/dashboard" replace />;
      case 'student':
        return <Navigate to="/dashboard" replace />;
      case 'application_admin':
      default:
        return <Navigate to="/unsupported-user-type" replace />;
    }
  }

  return <>{children}</>;
}

// Add UnsupportedUserType component
function UnsupportedUserTypePage() {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">User Type Not Supported</h2>
          <p className="mt-2 text-sm text-gray-600">
            Your user type "{user?.user_type}" is not currently supported by this application.
          </p>
        </div>
        <div className="mt-8 space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <p className="text-sm text-gray-500 mb-4">
              Please contact your administrator or try logging in with a different account.
            </p>
            <button
              onClick={handleLogout}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function RoleBasedRoutes() {
  const { flattenedRoutes, isLoading: areRoutesLoading } = useRoleRoutes();
  const { user } = useAuth();
  const renderRoutes = () => {
    return flattenedRoutes.map((route) => {
      if ('isLabel' in route || 'isGroup' in route) return null;
      if (!route.component) return null;

      return (
        <Route
          key={route.path}
          path={route.path}
          element={
            <Suspense fallback={<RouteLoader />}>
              <route.component />
            </Suspense>
          }
        />
      );
    });
  };

  if (user?.user_type === 'school_admin' && areRoutesLoading) {
    return <PermissionLoader />;
  }

  return (
    <Routes>
      {/* Auth routes */}
      <Route
        path="/auth"
        element={
          <GuestGuard>
            <AuthLayout />
          </GuestGuard>
        }
      >
        <Route index element={<Navigate to="/auth/login" replace />} />
        <Route path="login" element={<LoginPage />} />
      </Route>

      {/* Onboarding route */}
      <Route
        path="/onboarding"
        element={
          <AuthGuard>
            <OnboardingGuard>
              <AuthLayout />
            </OnboardingGuard>
          </AuthGuard>
        }
      >
        <Route index element={<OnboardingPage />} />
      </Route>

      {/* Unsupported user type route */}
      <Route
        path="/unsupported-user-type"
        element={
          <AuthGuard>
            <UnsupportedUserTypePage />
          </AuthGuard>
        }
      />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <AuthGuard>
            <OnboardingGuard>
              <RoleGuard>
                <MainLayout />
              </RoleGuard>
            </OnboardingGuard>
          </AuthGuard>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        {renderRoutes()}
      </Route>

      {/* 404 catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
